import { stableHash } from './stableHash'

/**
 * Deterministic background/foreground pair for a set of initials.
 *
 * Replaces `stringToColor()` for avatars. That helper computed a color arithmetically —
 * `'#' + (Math.abs(hash) % 0xffffff).toString(16)` — which has two problems that made initials
 * unreadable rather than merely off-brand:
 *
 *  1. `.toString(16)` drops leading zeros, so any hash whose remainder is below 0x100000 produced
 *     a five-or-fewer-digit string like `#a3f21` — not a valid CSS color, so it fell back to
 *     transparent and the initials sat on the page background.
 *  2. Even when valid, the value could land arbitrarily close to black or white, with no paired
 *     foreground — so the same avatar could be unreadable in light mode, dark mode, or both.
 *
 * Initials are the common case across the CRM (most external contacts have no photo), so this is
 * the path that has to hold contrast in both color modes. The ten pairs are defined as tokens in
 * assets/styles/tokens.scss with a light and a dark mirror, contrast-checked once, and picked here
 * by index — so a person keeps the same color in both modes and across sessions.
 */

/** Must match the number of `--avatar-N-*` token pairs defined in tokens.scss. */
export const AVATAR_PALETTE_SIZE = 10

/**
 * Stable index into the palette for a name.
 *
 * Same hash as the old stringToColor so a person's color is broadly familiar, but reduced modulo
 * the palette size instead of being used as a color directly. `>>> 0` keeps it non-negative
 * without `Math.abs`, which would map two different hashes onto the same index at the extremes.
 */
export const avatarPaletteIndex = (seed: string): number =>
  (stableHash(seed) >>> 0) % AVATAR_PALETTE_SIZE

export interface IAvatarColors {
  backgroundColor: string
  color: string
}

/**
 * CSS custom-property references for a name's avatar. Returned as `var(...)` strings rather than
 * resolved values so the pair follows a live light/dark toggle without re-rendering.
 *
 * An empty or whitespace-only name still gets a color — it renders the `?` placeholder, and a
 * transparent disc behind it reads as a broken image rather than a person with no name on file.
 */
export const avatarColors = (name: string | null | undefined): IAvatarColors => {
  const index = avatarPaletteIndex((name ?? '').trim().toLowerCase()) + 1
  return {
    backgroundColor: `var(--avatar-${index}-bg)`,
    color: `var(--avatar-${index}-text)`,
  }
}

/**
 * Up to two initials for a display name — first letter of the first and last word.
 *
 * Consolidates three separate implementations that disagreed on the edge cases: Avatar.tsx built
 * them from `firstName`/`lastName` fields and fell back to `'?'`; `stringAvatar` indexed
 * `name.split(' ')[1][0]` and threw on a trailing space (`'Ada '` → two parts, the second empty);
 * MemberAvatar took only the first character. Splitting on a collapsing whitespace regex is what
 * makes the trailing-space case safe.
 */
export const avatarInitials = (name: string | null | undefined): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'

  const first = parts[0][0]
  const last = parts.length > 1 ? parts[parts.length - 1][0] : ''
  return `${first}${last}`.toUpperCase()
}
